import { useEffect, useRef } from 'react';

import { dispatchOutdatedAlert } from '@/renderer/utils/alerts';
import { isNativePlatform } from '@/renderer/utils/isNativePlatform';

// The client bundle's build date (unix seconds), baked in at build time. Falls
// back to 0 in dev and whenever it is unknown, in which case we skip the check.
const APP_DATE = parseInt(`${import.meta.env.VITE_PUBLIC_ENV__APP_DATE}`, 10) || 0;

// How often to re-check while the tab is visible.
const CHECK_INTERVAL_MS = 60_000;

// Don't re-show the reload prompt more than once per this window per client,
// even across a burst of mandatory deploys (#1672). Persisted in localStorage.
const RATE_LIMIT_MS = 24 * 60 * 60 * 1000;
const LAST_SHOWN_KEY = 'ras:client-outdated-last-shown';

interface LatestVersion {
  appDate: number;
  // Whether the deploy asked to actively prompt for a reload. Absent/false on a
  // routine deploy, which is detected but applies silently on the next
  // navigation; only a mandatory deploy shows the toast.
  mandatory: boolean;
}

// Fetch the freshly-deployed client's build date and mandatory flag. The Worker
// serves /version.json uncached (worker/index.ts), so an open tab sees the
// latest deployed value rather than a cached copy.
async function fetchLatestVersion(): Promise<LatestVersion | null> {
  try {
    const res = await fetch('/version.json', { cache: 'no-store' });

    if (!res.ok) {
      return null;
    }

    const data = (await res.json()) as { appDate?: unknown; mandatory?: unknown };
    const date = parseInt(`${data?.appDate}`, 10);

    if (!Number.isFinite(date)) {
      return null;
    }

    return { appDate: date, mandatory: data?.mandatory === true };
  } catch {
    return null;
  }
}

// The last time this client showed the reload prompt (ms epoch, 0 if never).
// Storage being unavailable (private mode, etc.) must never suppress a genuine
// prompt, so any read failure reads as "never shown".
function readLastShownAt(): number {
  try {
    const last = parseInt(`${localStorage.getItem(LAST_SHOWN_KEY)}`, 10);

    return Number.isFinite(last) && last > 0 ? last : 0;
  } catch {
    return 0;
  }
}

function markOutdatedToastShown(now: number): void {
  try {
    localStorage.setItem(LAST_SHOWN_KEY, `${now}`);
  } catch {
    // Best-effort: without storage we simply can't rate-limit this client.
  }
}

/**
 * Whether the client-poll reload prompt should fire now (#1672). Pure so the
 * gating rules are unit-testable:
 *  - only when a strictly newer client bundle is deployed,
 *  - only when that deploy is flagged `mandatory` — routine deploys are still
 *    detected but stay silent and apply on the user's next navigation,
 *  - and at most once per {@link RATE_LIMIT_MS} per client.
 */
export function shouldPromptReload(params: {
  latestAppDate: number;
  currentAppDate: number;
  mandatory: boolean;
  lastShownAt: number;
  now: number;
}): boolean {
  const { latestAppDate, currentAppDate, mandatory, lastShownAt, now } = params;

  if (latestAppDate <= currentAppDate) {
    return false;
  }

  if (!mandatory) {
    return false;
  }

  if (lastShownAt > 0 && now - lastShownAt < RATE_LIMIT_MS) {
    return false;
  }

  return true;
}

/**
 * Prompts the user to reload when a newer *client* bundle has been deployed
 * (#1650).
 *
 * The WebSocket appDate check (WebSocketWatcher) only advances when the API
 * server redeploys, so client-only (viteclient) deploys never surfaced the
 * "Reload now" prompt even though the bundle changed under open tabs. This
 * polls a tiny, uncached /version.json published alongside the client bundle
 * on every viteclient deploy, decoupled from API rollouts.
 *
 * To avoid nagging on routine deploys, it only prompts when version.json is
 * flagged `mandatory`, and at most once per day per client (#1672); every other
 * newer deploy is detected but applies silently on the next navigation. See
 * {@link shouldPromptReload}.
 *
 * Renders nothing. Skips on native (a reload cannot swap the baked-in bundle)
 * and in dev / when APP_DATE is unknown (0). Reuses the existing outdated
 * toast, whose id is stable, so repeated dispatches are idempotent.
 */
export function ClientOutdatedCheck() {
  const checking = useRef(false);

  useEffect(() => {
    if (!APP_DATE || isNativePlatform()) {
      return;
    }

    let interval: ReturnType<typeof setInterval> | undefined;

    const check = async () => {
      // Avoid overlapping fetches (e.g. focus + interval firing together).
      if (checking.current) {
        return;
      }

      checking.current = true;

      try {
        const latest = await fetchLatestVersion();

        if (!latest) {
          return;
        }

        // Routine client deploys are detected but apply silently on the user's
        // next navigation; only a mandatory-flagged deploy prompts, and even
        // then at most once per day per client (#1672). A genuinely breaking
        // backend/contract change still prompts via the API's 417 gate (and the
        // WebSocketWatcher CLIENT_MIN_DATE check), which fires only when the
        // client is below CLIENT_MIN_DATE — not on a bare appDate bump (#1680).
        const now = Date.now();

        if (
          !shouldPromptReload({
            latestAppDate: latest.appDate,
            currentAppDate: APP_DATE,
            mandatory: latest.mandatory,
            lastShownAt: readLastShownAt(),
            now,
          })
        ) {
          return;
        }

        markOutdatedToastShown(now);
        dispatchOutdatedAlert();
      } finally {
        checking.current = false;
      }
    };

    const startInterval = () => {
      if (interval === undefined) {
        interval = setInterval(check, CHECK_INTERVAL_MS);
      }
    };

    const stopInterval = () => {
      if (interval !== undefined) {
        clearInterval(interval);
        interval = undefined;
      }
    };

    // Only poll while the tab is actually being looked at; re-check immediately
    // whenever the user returns to it (the common "left it open for hours" case).
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        check();
        startInterval();
      } else {
        stopInterval();
      }
    };

    // Always run one check on mount, even if the tab is currently backgrounded:
    // a tab left open in the background across a deploy is exactly the case we
    // want to catch, so the prompt is already waiting when the user returns.
    // Only the recurring poll is gated on visibility (below) to avoid background
    // network churn.
    check();

    if (document.visibilityState === 'visible') {
      startInterval();
    }

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', check);

    return () => {
      stopInterval();
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', check);
    };
  }, []);

  return null;
}

export default ClientOutdatedCheck;
